import { DetailsList, IColumn, MessageBar, MessageBarType, SelectionMode, Spinner } from '@fluentui/react';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { IPublishedLog, ITaskStatus, LogLevel, TaskStatusType } from '../../../API';
import { apiConfig } from '../../../authConfig';
import { IUserConfigPackage } from '../../../API';
import StatusIndicator, { StatusIndicatorType } from '../../controls/StatusIndicator';

export class LogStreamingState {
	
	public userConfig: IUserConfigPackage;
	public logMessages: IPublishedLog[] = [];
	public loading:boolean = true;
	private connection: HubConnection|null = null;

	public connectionMessage?:string;

	constructor(userConfig: IUserConfigPackage) {
		this.userConfig = userConfig;
		makeAutoObservable(this);
	}

	ensureInitialLoaded() {
		
		this.connection = new HubConnectionBuilder()
			.withUrl(apiConfig.uri + "/Hubs/Logging", { accessTokenFactory: this.userConfig.getToken.bind(this.userConfig) })
			.withAutomaticReconnect()
			.build();

		this.connection.on("logMessage", (messageJson) => {
			this.setConnectionMessage(undefined);
			this.addLogMessage(JSON.parse(messageJson));
		});

		this.connection.onreconnected(() => {
			this.setConnectionMessage(undefined);
		})

		this.connection.onreconnecting(() => {
			this.setConnectionMessage(undefined);
		})

		this.connection.onclose((error:Error|undefined) => {
			this.setConnectionMessage(error?.message);
		})

		this.connection.start();

		this.setLoading(false);
	}

	public setConnectionMessage(message: string | undefined) {
		this.connectionMessage = message;
	}

	public addLogMessage(arg0: IPublishedLog) {
		this.logMessages.unshift(arg0);
	}

	public setLoading(l:boolean) {
		this.loading = l;
	}
}

function LogStreaming({ state }: { state: LogStreamingState }) {
	
	let config:ITaskStatus[] = [
		{ name: LogLevel[LogLevel.Information], color: 'gray', type: TaskStatusType.Unset },
		{ name: LogLevel[LogLevel.Trace], color: 'gray', type: TaskStatusType.Unset },
		{ name: LogLevel[LogLevel.Warning], color: '#ef891c', type: TaskStatusType.Unset },
		{ name: LogLevel[LogLevel.Error], color: '#e93434', type: TaskStatusType.Unset },
		{ name: LogLevel[LogLevel.Critical], color: '#e93434', type: TaskStatusType.Unset },
		{ name: LogLevel[LogLevel.Debug], color: 'gray', type: TaskStatusType.Unset },
		{ name: LogLevel[LogLevel.None], color: 'gray', type: TaskStatusType.Unset },
	];

	function getLogLevel(logLevel:LogLevel) {
		let status = LogLevel[logLevel].toString();
		return <StatusIndicator config={config} type={StatusIndicatorType.BadgeSmall} status={status} />
	} 

	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);

	let columns: IColumn[] = [
		{
			key: 'logLevel',
			name: '',
			minWidth: 120,
			maxWidth: 120,
			onRender: (item: IPublishedLog) => <span>{getLogLevel(item.LogLevel)}</span>,
		},{
			key: 'date',
			name: 'Time',
			minWidth: 120,
			maxWidth: 120,
			onRender: (item: IPublishedLog) => <span>{new Date(item.DateTime).toLocaleTimeString()}</span>,
		},{
			key: 'message',
			name: 'Message',
			minWidth: 500,
			maxWidth: 700,
			isResizable:true,
			onRender: (item: IPublishedLog) => <span>{item.Message}</span>,
		},{
			key: 'user',
			name: 'User',
			minWidth: 120,
			maxWidth: 120,
			onRender: (item: IPublishedLog) => <span>{item.UserId}</span>,
		},
	];

	if(state.connectionMessage) {
		return <MessageBar messageBarType={MessageBarType.warning}>{"Connection lost: " + state.connectionMessage}</MessageBar>;
	}


	if (state.loading) {
		return <Spinner></Spinner>;
	}

	if(state.logMessages.length === 0) {
		return <span>Live log events will be shown here. Waiting for logs....</span>
	}
	
	return (
		<>
			<DetailsList selectionMode={SelectionMode.none} columns={columns} items={state?.logMessages || []} />
		</>
	);
}

export default observer(LogStreaming);
